import type { Chain } from '@ithildin/shared';

/**
 * Compose state and funded-escrow recovery, kept in localStorage.
 *
 * Both exist because the compose flow crosses a wallet popup: the tab can lose
 * focus, the extension can fail, the page can be closed. Losing a long message
 * to that is annoying; losing track of an escrow that is already funded on-chain
 * costs real money, because the sender has paid and has no record of it.
 */

const DRAFT_KEY = 'ithildin.draft.v1';
const FUNDED_KEY = 'ithildin.funded.v1';

export interface Draft {
  to: string;
  body: string;
  windowHours: number;
}

/** A payment that confirmed on-chain before the message was accepted. */
export interface FundedEscrow {
  toChain: Chain;
  toAddress: string;
  body: string;
  amountMicros: string;
  replyWindowHours: number;
  fundingTxSignature: string;
  vaultAddress: string;
  fundedAt: string;
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    // Private windows and blocked site data both throw; neither is worth
    // breaking the compose screen over.
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — the flow still works, it just cannot be resumed */
  }
}

function clear(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

export const draft = {
  load: () => read<Draft>(DRAFT_KEY),
  save: (d: Draft) => write(DRAFT_KEY, d),
  clear: () => clear(DRAFT_KEY),
};

export const fundedEscrow = {
  load: () => read<FundedEscrow>(FUNDED_KEY),
  save: (e: FundedEscrow) => write(FUNDED_KEY, e),
  clear: () => clear(FUNDED_KEY),
};
